import { forwardRef } from 'react'
import type { ReactNode } from 'react'

/**
 * `div.modal` / `div.mh` / `div.mb` / `div.mf` → `modal` / `modal-header` / `modal-body` /
 * `modal-footer` (process/08-COMPONENT-CATALOGUE.md, process/09-SCREEN-SPECS/_family-modals.md).
 *
 * `ModalDialog` is the catalogued dialog surface only — no fixed positioning, no scrim, no focus
 * trap. `src/screens/shared/ModalScrim.tsx`'s `Modal` is the thing every screen actually renders:
 * it owns the full-viewport overlay and the dialog semantics, and composes this underneath. Split
 * because the scrim itself has no catalogue entry (`_family-modals.md`'s own "Components — every
 * modal" list names `modal`/`modal-header`/`modal-body`/`modal-footer`/`button`/`note`/`icon` and
 * nothing else) — putting an uncatalogued root under `src/components/**` would be exactly what
 * `ui-contract/data-cmp-required` exists to catch, the same reason `Refusals.tsx`'s composition
 * helpers live under `src/screens/auth-shared/` rather than here.
 */
export const ModalDialog = forwardRef<HTMLDivElement, { labelledBy: string; maxWidth?: number; children: ReactNode }>(function ModalDialog(
  { labelledBy, maxWidth = 520, children },
  ref,
) {
  return (
    <div
      ref={ref}
      data-cmp="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      className="mx-auto bg-panel rounded-3 overflow-hidden"
      style={{ maxWidth, boxShadow: 'var(--shadow-3)' }}
    >
      {children}
    </div>
  )
})

/** `div.mh` → `modal-header`. `danger` is the one variant the prototype's CSS actually renders
 * (`.mh.danger`, `--danger-bg`/`--danger-bd`); `.mh.warn` parses in the markup but has no CSS rule
 * behind it, the same "inert prototype class" pattern `Strip`'s `ok`/`warn` already documented.
 *
 * `id` is the caller's own — `Modal`'s `labelledBy` must name the same one, so the pairing is
 * explicit at the call site rather than generated invisibly on either side. */
export function ModalHeader({ id, variant = 'default', heading, children }: { id: string; variant?: 'default' | 'danger'; heading: string; children?: ReactNode }) {
  return (
    <div
      data-cmp="modal-header"
      id={id}
      className={`py-7 px-9 border-b ${variant === 'danger' ? 'bg-danger-bg border-danger-bd' : 'border-bd'}`}
    >
      <h4 className="m-0 mb-2 text-heading font-semibold">{heading}</h4>
      {children && <p className="m-0 text-body text-tx2">{children}</p>}
    </div>
  )
}

/** `div.mb` → `modal-body`. */
export function ModalBody({ children }: { children: ReactNode }) {
  return (
    <div data-cmp="modal-body" className="py-8 px-9">
      {children}
    </div>
  )
}

/** `div.mf` → `modal-footer`. "dismiss on the left, commit on the right" (M1) — plain flow order
 * with `justify-end` achieves this without a11y-hostile visual reordering. */
export function ModalFooter({ children }: { children: ReactNode }) {
  return (
    <div data-cmp="modal-footer" className="flex gap-5 justify-end py-7 px-9 border-t border-bd bg-strip">
      {children}
    </div>
  )
}
